import React, { useState } from 'react'
import { DataSource, Folder } from '../types'
import { getButtonClasses, getButtonStyles } from '../constants'
import { FileIcon } from './common/FileIcon'
import { TbReportMedical, TbReport } from 'react-icons/tb';
import { RiDeleteBin2Fill } from "react-icons/ri";
import { IoFolderOpenSharp } from "react-icons/io5";
import { HiOutlineFolderDownload } from "react-icons/hi";
import { BsFolderPlus, BsCheckCircle } from "react-icons/bs";
import { MdOutlineRadioButtonUnchecked } from "react-icons/md";
import { SelectionControls, SelectionCheckbox } from './management';
import { useTranslate } from '../hooks/useTranslate';
import { useDataAdmission } from '../contexts/DataAdmissionContext';

// Add 3D card styles
const card3DStyles = `
.folder-card-3d {
  --button_radius: 8px;
  --button_color: #ffffff;
  --button_outline_color: #D31145;
  font-size: 17px;
  font-weight: bold;
  border: none;
  cursor: pointer;
  border-radius: var(--button_radius);
  background: transparent;
  width: 100%;
  height: 79px;
  position: relative;
}

.folder-card-3d .button_top {
  display: block;
  box-sizing: border-box;
  border: 2px solid var(--button_outline_color);
  border-radius: var(--button_radius);
  padding: 0.75em 1.5em;
  background: #f3f4f6;
  color: #374151;
  transform: translateY(-0.2em);
  transition: transform 0.1s ease;
  width: 100%;
  height: 100%;
  position: relative;
}

.folder-card-3d:hover .button_top {
  transform: translateY(-0.33em);
}

.folder-card-3d:active .button_top {
  transform: translateY(0);
}
`;

// Inject styles
if (typeof document !== 'undefined') {
    const styleId = 'card-3d-styles';
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = card3DStyles;
        document.head.appendChild(style);
    }
}

// Add CSS animations as a style tag
const animatedBorderStyles = `
@keyframes borderGlow {
  0% { border-color: #D31145; box-shadow: 0 0 5px #D31145; }
  25% { border-color: #e74c3c; box-shadow: 0 0 10px #e74c3c; }
  50% { border-color: #f39c12; box-shadow: 0 0 15px #f39c12; }
  75% { border-color: #e67e22; box-shadow: 0 0 10px #e67e22; }
  100% { border-color: #D31145; box-shadow: 0 0 5px #D31145; }
}

@keyframes borderMove {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.animated-folder-border {
  position: relative;
  background: linear-gradient(45deg, #D31145, #e74c3c, #f39c12, #e67e22, #D31145);
  background-size: 300% 300%;
  animation: borderMove 3s ease infinite;
  padding: 3px;
  border-radius: 28px 12px 28px 12px;
}

.animated-folder-border:hover {
  animation: borderMove 1.5s ease infinite, borderGlow 2s ease infinite;
}

.folder-card-inner {
  background: #d1d1d1;
  border-radius: 25px 9px 25px 9px;
  height: 100%;
  width: 100%;
}
`;

// Inject styles
if (typeof document !== 'undefined') {
    const styleId = 'animated-border-styles';
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = animatedBorderStyles;
        document.head.appendChild(style);
    }
}
// import CreateFolderForm from './CreateFolderForm' // deprecated inline form
import type { FolderNameValidationResult } from '../utils/validation/folderValidation'
import DeleteConfirmationModal from './DeleteConfirmationModal'
import SharedReportsSection from './SharedReportsSection'

interface MainViewProps {
    folders: Folder[]
    dataSources: DataSource[]
    showCreateFolder: boolean // now ignored (modal controls creation)
    onCreateFolder: (name: string) => Promise<FolderNameValidationResult & { folder?: Folder }>
    onCancelCreateFolder: () => void
    onFolderClick: (folder: Folder) => void
    onDeleteFolder: (folderId: number) => void
    onDataObjectClick: (dataObject: DataSource) => void
    onDeleteDataObject: (id: number) => void
    onShowCreateFolder: () => void
    onAddDataSource: () => void
    onRefreshDataObject?: (dataSource: DataSource) => Promise<void>
    disabled?: boolean
    // Multi-select props
    isSelecting?: boolean
    selectedIds?: number[]
    onCardSelection?: (id: number, checked: boolean) => void
    onToggleSelect?: () => void
    hasSelectableItems?: boolean
    selectedCount?: number
    onBulkMove?: () => void
    onBulkDelete?: () => void
    onSelectAll?: () => void
    onClearSelection?: () => void
}

const MainView: React.FC<MainViewProps> = ({
    folders,
    dataSources,
    onFolderClick,
    onDeleteFolder,
    onDataObjectClick,
    onDeleteDataObject,
    onShowCreateFolder,
    onAddDataSource,
    onRefreshDataObject,
    disabled = false,
    isSelecting = false,
    selectedIds = [],
    onCardSelection,
    onToggleSelect,
    hasSelectableItems = false,
    selectedCount = 0,
    onBulkMove,
    onBulkDelete,
    onSelectAll,
    onClearSelection
}) => {
    const [pendingDelete, setPendingDelete] = useState<
        { type: 'folder'; id: number; name: string } | { type: 'data'; id: number; name: string } | null
    >(null);
    const [justSelected, setJustSelected] = useState<number | null>(null); // Track recently selected card
    const { t } = useTranslate();
    const { sharedReports } = useDataAdmission();

    // Fixed color scheme - white folder cards, black text, neutral gray borders
    const getCardStyles = () => ({
        background: '#ffffff',
        color: '#000000',
        borderColor: '#d1d5db', // Gray border to match card styling
        borderWidth: '2px'
    })

    const getFontStyles = () => ({ color: '#000000' })

    const unassignedDataSources = dataSources.filter(ds => !ds.folderId)
    const sortedDataSources = unassignedDataSources

    return (
        <div className="space-y-1 px-2 -mt-4">
            {/* Folders Section */}
            <div>
                <div className="flex items-center mb-6">
                    <div className="flex items-center">
                        <h2 className="text-xl font-bold text-gray-800"><IoFolderOpenSharp className="inline mr-2 text-red-600 text-3xl" />{t('Folders')}</h2>
                        <div className="ml-4 h-px bg-gradient-to-r from-red-300 to-transparent flex-1"></div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                    {folders.length > 0 ? folders.map((folder) => (
                        <div
                            key={`folder-${folder.id}`}
                            className={`relative group ${isSelecting ? 'cursor-default' : 'cursor-pointer'
                                } rounded-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 shadow-xl hover:shadow-red-500/20 overflow-hidden`}
                            style={{
                                ...getCardStyles(),
                                border: '2px solid #d1d5db',
                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                transition: 'all 0.3s ease-out, box-shadow 0.3s ease-out',
                                minHeight: '79px',
                                background: '#E5E5E5' // Updated to requested color shade
                            }}
                            onClick={isSelecting ? undefined : () => onFolderClick(folder)}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = '0 20px 40px -10px rgba(211, 17, 69, 0.4), 0 10px 25px -5px rgba(211, 17, 69, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                            }}
                        >
                            <div className="p-2">
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center space-x-3 flex-1 min-w-0 pr-2">
                                        <div className="w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center flex-shrink-0">
                                            <TbReportMedical className="w-8 h-8 sm:w-12 sm:h-12 text-red-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-xs sm:text-sm font-bold truncate notranslate" style={getFontStyles()}>{folder.name}</h3>
                                            <p className="text-xs capitalize font-medium" style={{ ...getFontStyles(), opacity: 0.8 }}>{t('Folder')}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-1 flex-shrink-0">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setPendingDelete({ type: 'folder', id: folder.id, name: folder.name }) }}
                                            className="p-1 hover:bg-white hover:text-red-600 rounded transition-all duration-200 flex-shrink-0"
                                            style={{ color: '#666666' }}
                                            title="Delete folder"
                                        >
                                            <RiDeleteBin2Fill className="w-4 h-4 sm:w-5 sm:h-5" />
                                        </button>
                                    </div>
                                </div>
                                <div className="flex items-center justify-end mt-0.5">
                                    <div className="flex items-center space-x-2">
                                        <span className="inline-flex items-center px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold backdrop-blur-sm border whitespace-nowrap" style={{ ...getCardStyles(), ...getFontStyles(), borderColor: getFontStyles().color + '40' }}>{(folder as any).dataObjectCount ?? 0} {t('items')}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )) : null}
                </div>
            </div>

            {/* Reports Section */}
            {unassignedDataSources.length > 0 && (
                <div className="mt-32">
                    <div className="flex items-center justify-between mb-8 mt-16">
                        <div className="flex items-center">
                            <h2 className="text-xl font-bold text-gray-800"><TbReport className="inline mr-2 text-red-600 text-4xl" />{t('Data Objects')}</h2>
                            <div className="ml-4 h-px bg-gradient-to-r from-red-300 to-transparent flex-1"></div>
                        </div>

                        {/* Selection Controls - only show when in selection mode */}
                        {isSelecting ? (
                            <SelectionControls
                                selectedCount={selectedCount}
                                totalCount={dataSources.filter(ds => !ds.folderId).length}
                                onBulkMove={selectedCount > 0 ? onBulkMove : undefined}
                                onBulkDelete={selectedCount > 0 ? onBulkDelete : undefined}
                                onSelectAll={onSelectAll}
                                onClearSelection={onClearSelection}
                            />
                        ) : null}
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                        {sortedDataSources.map((dataSource, index) => {
                            const isSelected = selectedIds.includes(dataSource.id);
                            return (
                                <div
                                    key={`data-${dataSource.id}`}
                                    className={`rounded-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer transform hover:scale-105 hover:-translate-y-1 shadow-xl hover:shadow-red-500/20 relative overflow-hidden ${isSelected ? 'ring-2 ring-red-500 ring-offset-2' : ''}`}
                                    style={{
                                        ...getCardStyles(),
                                        animationDelay: `${(folders.length + index) * 150}ms`,
                                        border: '2px solid #d1d5db',
                                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                        transition: 'all 0.3s ease-out, box-shadow 0.3s ease-out',
                                        minHeight: '79px'
                                    }}
                                    onClick={(e) => {
                                        // Check if click originated from checkbox area
                                        if ((e.target as HTMLInputElement).type === 'checkbox') {
                                            return; // Don't handle card click if checkbox was clicked
                                        }

                                        // Prevent card click if this card was just selected via hover checkbox
                                        if (justSelected === dataSource.id) {
                                            setJustSelected(null); // Clear the flag
                                            return;
                                        }

                                        if (isSelecting && onCardSelection) {
                                            onCardSelection(dataSource.id, !selectedIds.includes(dataSource.id));
                                        } else if (!isSelecting && selectedIds.length === 0) {
                                            // Only allow card click if no items are selected and not in selection mode
                                            onDataObjectClick(dataSource);
                                        }
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.boxShadow = '0 20px 40px -10px rgba(211, 17, 69, 0.4), 0 10px 25px -5px rgba(211, 17, 69, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                                    }}
                                >
                                    {/* Hover Selection Button - appears on hover when not in selecting mode */}
                                    {!isSelecting && onCardSelection && (
                                        <div className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                                            <div className="mr-3">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        onCardSelection(dataSource.id, e.target.checked);
                                                        // Mark this card as just selected to prevent card click
                                                        if (e.target.checked) {
                                                            setJustSelected(dataSource.id);
                                                            // Clear the flag after a short delay
                                                            setTimeout(() => setJustSelected(null), 100);
                                                        }
                                                        // Trigger selection mode if not already active and we're selecting an item
                                                        if (!isSelecting && e.target.checked && onToggleSelect) {
                                                            onToggleSelect();
                                                        }
                                                    }}
                                                    className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
                                                    title="Select this item"
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {/* Selection Mode Checkbox - positioned absolutely at bottom-left */}
                                    {isSelecting && (
                                        <SelectionCheckbox
                                            id={dataSource.id}
                                            isSelected={isSelected}
                                            onSelectionChange={(id, checked) => {
                                                onCardSelection?.(id, checked);
                                            }}
                                        />
                                    )}
                                    <div className="p-2">
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center space-x-3 flex-1 min-w-0 pr-2">
                                                <div className="w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center flex-shrink-0">
                                                    <FileIcon
                                                        fileName={dataSource.originalFileName || dataSource.name}
                                                        fileType={dataSource.type}
                                                        className="w-8 h-8 sm:w-12 sm:h-12"
                                                    />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="text-xs sm:text-sm font-semibold truncate notranslate" style={getFontStyles()}>{dataSource.name}</h3>
                                                    <p className="text-xs font-medium notranslate" style={{ ...getFontStyles(), opacity: 0.8 }}>
                                                        {dataSource.type === 'databricks' ? 'Databricks' : dataSource.type.toUpperCase()}
                                                    </p>
                                                </div>
                                            </div>
                                            {!isSelecting && (
                                                <div className="flex items-center space-x-1 flex-shrink-0">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setPendingDelete({ type: 'data', id: dataSource.id, name: dataSource.name }) }}
                                                        className="p-1 hover:bg-red-500 hover:text-white rounded transition-all duration-200 flex-shrink-0"
                                                        style={{ color: '#000000' }}
                                                        title="Delete data object">
                                                        <RiDeleteBin2Fill className="w-4 h-4 sm:w-5 sm:h-5" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center justify-end mt-0.5">
                                            <div className="flex items-center space-x-1">
                                                <span className={`inline-flex items-center px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap ${dataSource.status === 'processed' ? 'bg-green-100 text-green-800' :
                                                    dataSource.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-red-100 text-red-800'
                                                    }`}>
                                                    {dataSource.status === 'processing' && (
                                                        <div className="loader-red loader-sm"></div>
                                                    )}
                                                    {dataSource.status === 'processed' ? t('Analyzed') :
                                                        dataSource.status === 'processing' ? t('Processing') :
                                                            dataSource.status === 'analyzing' ? t('Analyzing') :
                                                                dataSource.status === 'error' ? t('Error') : t('Unknown')}
                                                </span>
                                                {/* Refresh button for processing/analyzing status */}
                                                {(dataSource.status === 'processing' || dataSource.status === 'analyzing') && onRefreshDataObject && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onRefreshDataObject(dataSource);
                                                        }}
                                                        className="p-0.5 rounded-full hover:bg-gray-100 transition-colors"
                                                        title="Refresh status"
                                                    >
                                                        <svg className="w-3.5 h-3.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Shared Reports Section */}
            <SharedReportsSection reports={sharedReports} />

            {/* Empty State */}
            {folders.length === 0 && unassignedDataSources.length === 0 && (
                <div className="text-center py-12">
                    <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 shadow-xl" style={{ ...getCardStyles(), border: '2px solid #d1d5db', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={getFontStyles()}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                    </div>
                    <h3 className="text-lg font-medium mb-2" style={getFontStyles()}>{t('Your workspace is empty')}</h3>
                    <p className="text-sm mb-6" style={{ ...getFontStyles(), opacity: 0.8 }}>{t('Create folders to organize your data or upload your first data object')}</p>
                </div>
            )}

            <DeleteConfirmationModal
                isOpen={!!pendingDelete}
                onClose={() => setPendingDelete(null)}
                onConfirm={() => {
                    if (pendingDelete) {
                        if (pendingDelete.type === 'folder') {
                            onDeleteFolder(pendingDelete.id)
                        } else {
                            onDeleteDataObject(pendingDelete.id)
                        }
                    }
                }}
                entityName={pendingDelete?.name || ''}
                title={pendingDelete?.type === 'folder' ? t('Delete Folder') : t('Delete Data Object')}
                confirmLabel={pendingDelete?.type === 'folder' ? t('Delete Folder Permanently') : t('Delete Permanently')}
            />
        </div>
    )
}

export default MainView